import { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Edit2, Trash2, UserPlus, Users, Activity, BarChart2, Zap } from 'lucide-react';
import type { AdminUser } from 'types';
import { useAdminUsers } from 'hooks/useAdminUsers';
import { useAdminRoles } from 'hooks/useAdminRoles';
import { deleteUser } from 'services/usersApi';
import { getLlmUsageStatsTotal, type LlmUsageStatsTotal } from 'services/llmApi';
import { globalConfirm, globalSnackbar } from 'components/GlobalSnackbar';
import { avatarColor } from 'utils/avatarColor';
import UserDrawer from './UserDrawer';

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}М`;
  if (n >= 1000) return `${Math.round(n / 1000)}К`;
  return String(n);
}

function formatDate(createdAt: number | null): string {
  if (!createdAt) return '—';
  return new Date(createdAt * 1000).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

const TABLE_COLS = '2fr 1.6fr 110px 110px 76px';

function UserAvatar({ user, size = 32 }: { user: AdminUser; size?: number }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: avatarColor(user.id),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: '#fff',
        fontSize: size * 0.32,
        fontWeight: 700,
        letterSpacing: '0.02em',
      }}
    >
      {user.avatarInitials}
    </Box>
  );
}

export default function UsersTab() {
  const { users, isLoading, error, refetch } = useAdminUsers();
  const { roles, isLoading: rolesLoading } = useAdminRoles();
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [creating, setCreating] = useState(false);
  const [statsTotal, setStatsTotal] = useState<LlmUsageStatsTotal | undefined>();

  useEffect(() => {
    let active = true;
    getLlmUsageStatsTotal()
      .then((data) => {
        if (active) setStatsTotal(data);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const totalRequests = statsTotal?.action_count ?? 0;
  const totalTokens = (statsTotal?.tokens_in_total ?? 0) + (statsTotal?.tokens_out_total ?? 0);
  const activeCount = users.filter((u) => u.isActive).length;

  const handleDelete = (user: AdminUser) => {
    globalConfirm({
      title: 'Удаление пользователя',
      message: `Удалить пользователя «${user.name || user.email}»? Действие необратимо.`,
      confirmText: 'Удалить',
      onCancel: () => undefined,
      onConfirm: async () => {
        try {
          await deleteUser(user.id);
          globalSnackbar({ message: 'Пользователь удалён', severity: 'success' });
          refetch();
        } catch (err) {
          globalSnackbar({
            message: err instanceof Error ? err.message : 'Ошибка удаления пользователя',
            severity: 'error',
          });
        }
      },
    });
  };

  const kpiCards = [
    { label: 'Всего пользователей', value: String(users.length), icon: <Users size={17} /> },
    { label: 'Активных', value: String(activeCount), icon: <Activity size={17} /> },
    { label: 'Суммарно запросов', value: fmt(totalRequests), icon: <BarChart2 size={17} /> },
    { label: 'Суммарно токенов', value: fmt(totalTokens), icon: <Zap size={17} /> },
  ];

  return (
    <>
      {/* Кнопка создания */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<UserPlus size={15} />}
          onClick={() => setCreating(true)}
          disabled={rolesLoading}
          sx={{
            bgcolor: 'var(--color-primary)',
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'none',
            px: 2,
            py: 1,
            '&:hover': { bgcolor: '#4a4de0', boxShadow: 'none' },
          }}
        >
          Добавить пользователя
        </Button>
      </Box>

      {/* KPI */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
        {kpiCards.map((card) => (
          <Box
            key={card.label}
            sx={{
              background: '#FFFFFF',
              border: '1px solid rgba(20,116,255,0.10)',
              boxShadow: '0 2px 16px rgba(20,116,255,0.08), 0 1px 4px rgba(0,0,0,0.03)',
              borderRadius: 'var(--radius-lg)',
              p: 2.5,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 'var(--radius-md)',
                background:
                  'linear-gradient(135deg, rgba(20,116,255,0.10) 0%, rgba(20,116,255,0.08) 100%)',
                border: '1px solid rgba(20,116,255,0.14)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-primary)',
                flexShrink: 0,
              }}
            >
              {card.icon}
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: 'var(--color-text-primary)',
                  lineHeight: 1,
                  letterSpacing: '-0.4px',
                }}
              >
                {card.value}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)', mt: 0.5 }}>
                {card.label}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Таблица */}
      <Box
        sx={{
          background: '#FFFFFF',
          border: '1px solid var(--color-border)',
          boxShadow: '0 2px 16px rgba(20,116,255,0.08), 0 1px 4px rgba(0,0,0,0.03)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Пользователи</Typography>
          <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            {users.length} записей
          </Typography>
        </Box>

        {/* Заголовки колонок */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: TABLE_COLS,
            px: 2.5,
            py: 1,
            bgcolor: 'var(--color-surface-subtle)',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          {['Пользователь', 'Роли', 'Статус', 'Создан', ''].map((h, idx) => (
            <Typography
              key={h || `col-${idx}`}
              sx={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--color-text-muted)',
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>

        {(isLoading || rolesLoading) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: 'var(--color-primary)' }} />
          </Box>
        )}

        {!isLoading && error && (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, color: '#dc2626' }}>{error}</Typography>
          </Box>
        )}

        {!isLoading && !rolesLoading && !error && users.length === 0 && (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              Нет пользователей — добавьте первого через кнопку выше
            </Typography>
          </Box>
        )}

        {!isLoading &&
          !rolesLoading &&
          users.map((user, i) => (
            <Box
              key={user.id}
              onClick={() => setEditingUser(user)}
              sx={{
                display: 'grid',
                gridTemplateColumns: TABLE_COLS,
                px: 2.5,
                py: 1.75,
                alignItems: 'center',
                borderBottom: i < users.length - 1 ? '1px solid var(--color-border)' : 'none',
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: 'var(--color-surface-subtle)' },
              }}
            >
              {/* Пользователь */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                <UserAvatar user={user} size={32} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'var(--color-text-primary)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {user.name || '—'}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: 'var(--color-text-muted)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {user.email}
                  </Typography>
                </Box>
              </Box>

              {/* Роли */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pr: 1 }}>
                {user.isAdmin && (
                  <Box
                    sx={{
                      bgcolor: 'rgba(220,38,38,0.10)',
                      color: '#dc2626',
                      borderRadius: '6px',
                      px: 1,
                      py: '3px',
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    Администратор
                  </Box>
                )}
                {user.roles
                  .filter((r) => r.name !== 'admin')
                  .map((r) => (
                    <Box
                      key={r.id}
                      sx={{
                        bgcolor: 'rgba(20,116,255,0.10)',
                        color: '#1474FF',
                        borderRadius: '6px',
                        px: 1,
                        py: '3px',
                        fontSize: 11,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.title || r.name}
                    </Box>
                  ))}
                {user.roles.length === 0 && !user.isAdmin && (
                  <Typography sx={{ fontSize: 12, color: 'var(--color-text-placeholder)' }}>
                    —
                  </Typography>
                )}
              </Box>

              {/* Статус */}
              <Box sx={{ display: 'inline-flex' }}>
                <Box
                  sx={{
                    bgcolor: user.isActive ? 'rgba(5,150,105,0.10)' : 'rgba(107,114,128,0.10)',
                    color: user.isActive ? '#059669' : '#6b7280',
                    border: `1px solid ${user.isActive ? 'rgba(5,150,105,0.20)' : 'rgba(107,114,128,0.18)'}`,
                    borderRadius: '6px',
                    px: 1,
                    py: '3px',
                    fontSize: 11,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.isActive ? 'Активен' : 'Отключён'}
                </Box>
              </Box>

              {/* Создан */}
              <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                {formatDate(user.createdAt)}
              </Typography>

              {/* Действия */}
              <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingUser(user);
                  }}
                  sx={actionBtnSx}
                >
                  <Edit2 size={13} />
                </Box>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(user);
                  }}
                  sx={{
                    ...actionBtnSx,
                    '&:hover': {
                      bgcolor: 'rgba(220,38,38,0.08)',
                      borderColor: 'rgba(220,38,38,0.4)',
                      color: '#dc2626',
                    },
                  }}
                >
                  <Trash2 size={13} />
                </Box>
              </Box>
            </Box>
          ))}
      </Box>

      {editingUser && (
        <UserDrawer
          user={editingUser}
          availableRoles={roles}
          onClose={() => setEditingUser(null)}
          onSaved={refetch}
        />
      )}
      {creating && (
        <UserDrawer
          user={null}
          availableRoles={roles}
          onClose={() => setCreating(false)}
          onSaved={refetch}
        />
      )}
    </>
  );
}

const actionBtnSx = {
  width: 28,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text-muted)',
  cursor: 'pointer',
  transition: 'all 0.15s',
  '&:hover': {
    bgcolor: 'var(--color-primary-tint)',
    borderColor: 'var(--color-primary)',
    color: 'var(--color-primary)',
  },
} as const;
